import React from "react";
import { Link } from "react-router";
import { CircularProgress, RaisedButton } from "material-ui";
import assign from "lodash/assign";
import chunk from "lodash/chunk";
import { colors } from "material-ui/styles";
import Helmet from "react-helmet";

import { makeUrl } from "../utils/api";
import {
  capitalize,
  formatInsertables,
  getPriceForItemAtQuantity,
  formatPrice,
  dashify,
  isApp
} from "../util";
import TenantIcon from "./partials/icons/tenant-icon.jsx";
import InjectedTenantHTML from "./partials/injected-tenant-html.jsx";
import LinkLike from "./partials/link-like.jsx";
import PassButton from "./partials/pass-button.jsx";
import ModeSwitcher from "./partials/mode-switcher.jsx";

import connectToStores from "alt-utils/lib/connectToStores";
import PassStore from "../stores/PassStore";
import PassActions from "../actions/PassActions";
import PersonStore from "../stores/PersonStore";

@connectToStores
class MembershipSales extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hasGlobalMembershipDiscount: false,
      discountCode: null,
      discountError: null
    };

    this.applyGlobalPassDiscount = this.applyGlobalPassDiscount.bind(this);
    this.getStyles = this.getStyles.bind(this);
    this._renderPassItem = this._renderPassItem.bind(this);
  }

  static get contextTypes() {
    return {
      muiTheme: React.PropTypes.object,
      router: React.PropTypes.object.isRequired,
      tenant: React.PropTypes.object,
      appSettings: React.PropTypes.object,
      isLarge: React.PropTypes.bool
    };
  }

  static getStores() {
    return [PassStore];
  }

  static getPropsFromStores() {
    let { hasUpdatedAllTypes } = PassStore.getState();

    return {
      hasUpdatedAllTypes,
      getPassTypeByCode: PassStore.getPassTypeByCode
    };
  }

  componentDidMount() {
    // Commenting this out because it is blocking legitimate button clicks
    // but leaving for refernece because there are potentially situations where 
    // we need to block online pass sales in the iOS app in the future
    //if (isApp()) {
    //  this.context.router.replace("/");
    //}
    PassActions.fetchPassTypes();
  }

  componentWillReceiveProps(newProps) {
    if (newProps.location.query._apply_gpd) {
      this.applyGlobalPassDiscount();
      return;
    }

    if (newProps.location.query.unlock_code) {
      newProps.location.query.discount_code =
        newProps.location.query.unlock_code;
    }

    if (newProps.location.query.discount_code) {
      this.setState({ discountCode: newProps.location.query.discount_code });
    } else if (newProps.location.query._gpd) {
      this.setState({
        hasGlobalMembershipDiscount: true,
        discountError: null,
        discountCode: atob(newProps.location.query._gpd)
      });
      this.context.router.replace({ pathname: newProps.location.pathname });
    } else if (newProps.location.query._gpd_e) {
      this.setState({
        hasGlobalMembershipDiscount: false,
        discountCode: null,
        discountError: atob(newProps.location.query._gpd_e)
      });
      this.context.router.replace({ pathname: newProps.location.pathname });
    }

    if (newProps.hasUpdatedAllTypes && !this.state.hasScrolled) {
      this.setState({ hasScrolled: true });
      setTimeout(() => {
        if (window.location.hash) {
          let el = document.getElementById(
            window.location.hash.replace(/#(?:buy-)?/g, "")
          );
          if (el) {
            el.scrollIntoView();
            if (window.scrollY) {
              window.scroll(0, window.scrollY - 72);
            }
          }
        }
      }, 0);
    }
  }

  applyGlobalPassDiscount() {
    if (!PersonStore.isLoggedIn()) {
      this.context.router.push({
        pathname: "/login",
        query: { next: `${this.props.location.pathname}?_apply_gpd=true` }
      });
      return;
    }

    window.location.href = makeUrl({
      path: "apply_global_pass_coupon",
      query: {
        token: PersonStore.getState().token,
        redirect: [
          window.location.protocol,
          "//",
          window.location.host,
          window.location.pathname
        ].join("")
      }
    });
  }

  getStyles() {
    let primaryColor = this.context.muiTheme.rawTheme.palette.primary1Color,
      { isLarge, tenant, muiTheme } = this.context,
      { prepareStyles } = muiTheme,
      useFixedBuyButtons = tenant.membership_sales_fixed_buttons;

    return {
      loading: {
        padding: 10,
        paddingTop: 30,
        textAlign: "center"
      },
      appMessage: {
        padding: 10,
        paddingTop: 30,
        textAlign: "center",
        fontSize: "12pt"
      },
      container: {
        padding: isLarge ? 10 : 25,
        fontSize: "11pt",
        marginTop: isLarge ? "-0.5em" : "-2em"
      },
      title: {
        textAlign: "center",
        textTransform: "capitalize",
        fontSize: 30,
        margin: "1em 0 0.5em",
        lineHeight: 1
      },
      itemContainer: {
        width: "100%",
        border: "1px solid #ccc",
        borderRadius: 4,
        display: isLarge ? "flex" : null
      },
      singleItemContainer: {
        maxWidth: 500,
        margin: "0 auto"
      },
      nextRowItemContainer: {
        margin: "1em auto 0"
      },
      item: prepareStyles({
        flex: isLarge ? 1 : null,
        width: isLarge ? "33.3333%" : "100%",
        display: useFixedBuyButtons
          ? "flex"
          : isLarge
            ? "inline-block"
            : "block",
        flexDirection: "column",
        boxSizing: "border-box",
        borderRight: isLarge && "1px solid #ccc",
        borderBottom: !isLarge && "1px solid #ccc",
        padding: 20
      }),
      icon: {
        height: "64px",
        display: "block",
        verticalAlign: "top",
        fontSize: 64,
        textAlign: "center"
      },
      header: {
        textAlign: "center",
        marginTop: 5,
        fontSize: 19,
        textTransform: "uppercase"
      },
      headerNoDescription: {
        marginBottom: 0
      },
      content: prepareStyles({
        flex: useFixedBuyButtons ? "1" : undefined
      }),
      list: {
        margin: 0,
        paddingLeft: 10
      },
      line: {
        margin: "5px 0"
      },
      buttonContainer: {
        textAlign: "center",
        marginTop: 20
      },
      introduction: {
        margin: "0 0 1em"
      },
      notice: {
        margin: "1em 0"
      },
      highlightNotice: {
        padding: 10,
        backgroundColor: colors.yellow500
      },
      couponNotice: {
        backgroundColor: colors.green500,
        color: "#fff",
        textAlign: "center",
        margin: "0 -10px"
      },
      couponError: {
        backgroundColor: colors.red600
      },
      singleItemNotice: {
        maxWidth: 500,
        marginLeft: "auto",
        marginRight: "auto"
      },
      saleRibbon: {
        width: 180,
        position: "absolute",
        textAlign: "center",
        lineHeight: "30px",
        letterSpacing: "1px",
        color: tenant.invert_colors ? "#000" : "#fff",
        top: 10,
        right: -65,
        left: "auto",
        transform: "rotate(45deg)",
        WebkitTransform: "rotate(45deg)",
        backgroundColor: primaryColor,
        fontWeight: "bold",
        textTransform: "uppercase",
        fontSize: 15
      }
    };
  }

  _renderPassItem(code, i, items) {
    let styles = this.getStyles(),
      lastItemStyle = assign({}, styles.item, {
        borderBottom: "",
        borderRight: ""
      }),
      membershipIcons = this.context.tenant.membership_icons,
      defaultDetails = this.context.tenant["membership_sales_details--default"],
      noMembershipDetails = this.context.tenant["membership_sales_details--no_memberships"],
      membershipDetails = this.context.tenant.membership_sales_details;

    let fixedCode = code.replace(/--.+$/, "");

    if (membershipDetails && membershipDetails[code] && membershipDetails[code].quantity) {
      membershipDetails[code].preset_quantity = membershipDetails[code].quantity;
    }

    let details =
      code === "no_passes"
        ? noMembershipDetails
        : Object.assign(
          {},
          defaultDetails,
          membershipDetails[code],
          this.props.getPassTypeByCode(fixedCode)
        ),
      price =
        getPriceForItemAtQuantity(details, details.preset_quantity || 1) *
        (details.preset_quantity || 1),
      buyLabel =
        details.buy_label &&
        formatInsertables(details.buy_label, {
          price: () => formatPrice(price, /* short */ true)
        }),
      isDisabled = false;

    // User-entered title takes precendence
    if (details.title) {
      details.name = details.title;
    }

    // Replace hyphens with non-breaking hyphens to prevent goofy line breaks
    if (details.name) {
      details.name = details.name.replace(/-/g, "‑");
    }

    // Sold out pass
    if (details.quantity_remaining <= 0) {
      buyLabel = "Sold Out";
      isDisabled = true;
    }

    return (
      <div
        key={`${code}_${i}`}
        style={Object.assign(
          {},
          i === items.length - 1 ? lastItemStyle : styles.item,
          { position: "relative", overflow: "hidden" }
        )}
      >
        {details.sale_price ? <div style={styles.saleRibbon}>Sale</div> : null}
        {membershipIcons[code] ? (
          <TenantIcon name={membershipIcons[code]} style={styles.icon} />
        ) : null}
        <h4
          id={dashify((code || "").toLowerCase())}
          style={Object.assign(
            {},
            styles.header,
            !details.description.__html && styles.headerNoDescription
          )}
        >
          {details.name}
        </h4>
        {details.description.__html ? (
          <InjectedTenantHTML
            style={styles.content}
            content={details.description}
          />
        ) : null}
        {buyLabel ? (
          <div style={styles.buttonContainer}>
            <PassButton
              disabled={isDisabled}
              label={buyLabel}
              code={fixedCode}
              price={price}
              details={details}
              target={{
                pathname: `/${this.context.tenant.memberships_url_pathname}/buy/${fixedCode}`,
                query: Object.assign(
                  {},
                  details.preset_quantity &&
                  details.preset_quantity > 1 && {
                    quantity: details.preset_quantity
                  },
                  this.state.discountCode && {
                    discount_code: this.state.discountCode
                  }
                ),
                state: { fromMainListing: true }
              }}
            />
            {details.sale_price ? (
              <div style={{ fontStyle: "italic", marginTop: 10 }}>
                Regular:{" "}
                <span style={{ textDecoration: "line-through" }}>
                  {formatPrice(details.price, true)}
                </span>{" "}
                <strong>{formatPrice(price, true)}</strong>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  render() {
    let styles = this.getStyles();
    let sectionsToDisplay = this.context.tenant.membership_sales_sections;

    if (!this.props.hasUpdatedAllTypes) {
      return (
        <div style={styles.loading}>
          <CircularProgress
            mode="indeterminate"
            size={119}
            thickness={7}
            style={{ margin: 10.5 }}
          />
        </div>
      );
    }

    if (isApp()
      && this.context.appSettings.enable_virtual_festival
      && !this.context.tenant.pass_sales_allow_in_app
    ) {
      return (
        <div style={styles.appMessage}>
          This functionality is not available in the App.
        </div>
      );
    }

    // Show no memberships error text if none configured
    if (!sectionsToDisplay || !sectionsToDisplay.length) {
      sectionsToDisplay = [
        { title: capitalize(this.context.tenant.display_memberships), memberships: [] }
      ];
    }
    if (!sectionsToDisplay || (sectionsToDisplay[0].memberships && !sectionsToDisplay[0].memberships.length)) {
      sectionsToDisplay[0].memberships.push("no_passes");
    }

    // Global pass discount
    if (this.context.tenant.global_membership_discount_enabled) {
      sectionsToDisplay[0].notice_component = (
        <div style={{ textAlign: "center" }}>
          {formatInsertables(this.context.tenant.global_membership_discount_cta, {
            apply_discount_link: label => (
              <LinkLike onClick={this.applyGlobalPassDiscount}>
                {label}
              </LinkLike>
            ),
            link: (href, label) => (
              <a href={href} target="_blank">
                {label}
              </a>
            )
          })}
        </div>
      );
    }

    return (
      <div>
        <Helmet title={sectionsToDisplay[0].title} />
        {this.props.children
          ? React.cloneElement(this.props.children, {
            appSettings: this.props.appSettings
          })
          : undefined}
        {this.context.tenant.marquee_enabled ? (
          <ModeSwitcher value="memberships" style={{ marginBottom: "-2em" }} />
        ) : null}
        <div style={styles.container}>
          {this.state.discountCode || this.state.discountError ? (
            <div
              style={assign(
                {},
                styles.notice,
                styles.highlightNotice,
                styles.couponNotice,
                this.state.discountError && styles.couponError
              )}
            >
              {this.state.discountError
                ? `Unable to apply discount: ${this.state.discountError}`
                : this.state.hasGlobalMembershipDiscount
                  ? this.context.tenant.global_membership_discount_applied_message
                  : `Good news! The ${this.props.location.query.unlock_code
                    ? "unlock code"
                    : "discount code"
                  } "${this.state.discountCode
                  }" will automatically be applied to your order.`}
            </div>
          ) : null}
          {sectionsToDisplay.map(section => {
            return (
              <div>
                <h2
                  id={dashify((section.title || "").toLowerCase())}
                  style={styles.title}
                >
                  {section.title}
                </h2>
                {section.introduction && section.introduction.__html ? (
                  <InjectedTenantHTML
                    style={assign(
                      {},
                      styles.introduction,
                      section.introduction_highlight && styles.highlightNotice
                    )}
                    content={section.introduction}
                  />
                ) : null}
                {section.notice_component ? (
                  <div
                    style={assign({}, styles.notice, styles.highlightNotice)}
                  >
                    {section.notice_component}
                  </div>
                ) : null}
                {(this.context.isLarge
                  ? chunk(section.memberships, 4)
                  : [section.memberships]
                ).map((items, i) => {
                  if (!items) {
                    items = [];
                  }
                  return (
                    <div
                      key={i}
                      style={assign(
                        {},
                        styles.itemContainer,
                        items.length === 1 && styles.singleItemContainer,
                        i > 0 && styles.nextRowItemContainer
                      )}
                    >
                      {items.map(this._renderPassItem)}
                    </div>
                  );
                })}
                {section.notice && section.notice.__html ? (
                  <InjectedTenantHTML
                    style={assign(
                      {},
                      styles.notice,
                      section.notice_highlight && styles.highlightNotice,
                      section.memberships.length === 1 && styles.singleItemNotice
                    )}
                    content={section.notice}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

export default MembershipSales;



// WEBPACK FOOTER //
// ./src/app/components/membership-sales.jsx